// useHistoryNavigation.js
// Custom hook to sync React view state with browser history
// Enables browser back/forward buttons to navigate between views

import { useEffect, useRef, useCallback } from 'react';

/**
 * Syncs a view state variable with the browser's history API.
 * When the view changes, it pushes a new history entry.
 * When the user presses back/forward, it updates the view state.
 * 
 * @param {string} currentView - The current view/tab name
 * @param {function} setCurrentView - State setter for the view
 * @param {string} defaultView - The default/home view name
 * @param {object} options - Optional additional state to track (e.g. sub-views)
 */
export function useHistoryNavigation(currentView, setCurrentView, defaultView, options = {}) {
  const isPopState = useRef(false);
  const lastPushedView = useRef(currentView);
  const { onPopState } = options;

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      isPopState.current = true;
      const state = event.state;
      if (state && state.view) {
        setCurrentView(state.view);
        // Call optional handler for additional state restoration
        if (onPopState) {
          onPopState(state);
        }
      } else {
        // No state means we're at the initial entry - go to default view
        setCurrentView(defaultView);
        if (onPopState) {
          onPopState({ view: defaultView });
        }
      }
      // Reset flag after a tick to allow the state update to process
      setTimeout(() => { isPopState.current = false; }, 0);
    };

    window.addEventListener('popstate', handlePopState);
    
    // Replace the current history entry with the initial view state
    window.history.replaceState({ view: currentView }, '', window.location.pathname);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Only run on mount

  // Push new history entry when view changes (but not from popstate)
  useEffect(() => {
    if (isPopState.current) {
      // This change was triggered by back/forward button, don't push again
      return;
    }
    if (currentView !== lastPushedView.current) {
      window.history.pushState({ view: currentView }, '', window.location.pathname);
      lastPushedView.current = currentView;
    }
  }, [currentView]);

  // Helper to navigate with additional state
  const navigateToView = useCallback((view, extraState = {}) => {
    const state = { view, ...extraState };
    window.history.pushState(state, '', window.location.pathname);
    lastPushedView.current = view;
    setCurrentView(view);
  }, [setCurrentView]);

  return { navigateToView };
}